import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

import { CONTRACTOR_ROLES, roleLabel } from "@/constants/roles";
import { VENDOR_TYPES, vendorTypeLabel } from "@/constants/vendorTypes";

const ROLES = CONTRACTOR_ROLES.map((r) => r.value);
const CONTEXT_TYPES = [
  { value: "job", label: "Job" },
  { value: "change_order", label: "Change Order" },
  { value: "non_job", label: "Non-Job" },
];
const contextTypeLabel = (v) => CONTEXT_TYPES.find((t) => t.value === v)?.label || v;

const emptyForm = { name: "", min: 0, max: "", roles: [], vendorTypes: [], contextTypes: [], escalationHours: "" };

export default function ApprovalRules() {
  const [rules, setRules] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/contractor/approval-rules").then((r) => setRules(r.data));
  useEffect(() => { load(); }, []);

  const toggle = (field, value) => {
    setForm((f) => ({ ...f, [field]: f[field].includes(value) ? f[field].filter((x) => x !== value) : [...f[field], value] }));
  };

  const create = async () => {
    if (!form.name || form.roles.length === 0) return toast.error("Name and at least one approver role required");
    try {
      await api.post("/contractor/approval-rules", {
        name: form.name, threshold_min: Number(form.min), threshold_max: form.max ? Number(form.max) : null,
        approver_roles: form.roles, vendor_types: form.vendorTypes, context_types: form.contextTypes,
        escalation_hours: form.escalationHours ? Number(form.escalationHours) : null,
      });
      toast.success("Approval rule created");
      setShow(false); setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const remove = async (id) => {
    await api.delete(`/contractor/approval-rules/${id}`);
    load();
  };

  const ruleSummary = (r) => {
    const parts = [];
    parts.push(`$${r.threshold_min.toLocaleString("en-US")}${r.threshold_max ? ` – $${r.threshold_max.toLocaleString("en-US")}` : "+"}`);
    if (r.vendor_types?.length) parts.push(r.vendor_types.map(vendorTypeLabel).join("/"));
    if (r.context_types?.length) parts.push(r.context_types.map((t) => (["job", "change_order", "non_job"].includes(t) ? contextTypeLabel(t) : t)).join("/"));
    const routing = `→ ${r.approver_roles.map(roleLabel).join(", ")}`;
    const escalation = r.escalation_hours ? ` · escalates after ${r.escalation_hours}h` : "";
    return `${parts.join(" · ")} ${routing}${escalation}`;
  };

  return (
    <div className="p-6 md:p-8" data-testid="approval-rules-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Approval Rules</h1>
          <p className="text-slate-500 text-sm mt-1">Define thresholds and routing for purchase approvals.</p>
        </div>
        <button onClick={() => { setForm(emptyForm); setShow(true); }} data-testid="new-rule-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
          <Plus size={14} /> New Rule
        </button>
      </div>

      <div className="mt-7 space-y-3" data-testid="rules-list">
        {rules.map((r) => (
          <div key={r.id} className="border border-slate-200 rounded-xl p-4 flex items-center justify-between" data-testid={`rule-card-${r.id}`}>
            <div>
              <div className="font-semibold text-[14px]">{r.name}</div>
              <div className="text-[12.5px] text-slate-500 mt-1" data-testid={`rule-summary-${r.id}`}>{ruleSummary(r)}</div>
            </div>
            <button onClick={() => remove(r.id)} data-testid={`delete-rule-${r.id}`} className="text-slate-400 hover:text-red-500"><Trash2 size={16} /></button>
          </div>
        ))}
        {rules.length === 0 && <div className="text-slate-400 text-sm text-center py-10">No approval rules configured.</div>}
      </div>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent data-testid="new-rule-modal">
          <DialogHeader><DialogTitle>New Approval Rule</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2 max-h-[70vh] overflow-y-auto ss-scrollbar pr-1">
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Rule name" data-testid="rule-name-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <div className="grid grid-cols-2 gap-3">
              <input type="number" value={form.min} onChange={(e) => setForm({ ...form, min: e.target.value })} placeholder="Min amount" data-testid="rule-min-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              <input type="number" value={form.max} onChange={(e) => setForm({ ...form, max: e.target.value })} placeholder="Max amount (blank = no limit)" data-testid="rule-max-input" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>

            <div>
              <label className="text-[13px] font-medium">Approver Roles *</label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {ROLES.map((r) => (
                  <label key={r} className="flex items-center gap-1.5 text-[12.5px] border border-slate-200 rounded-full px-2.5 py-1">
                    <input type="checkbox" checked={form.roles.includes(r)} onChange={() => toggle("roles", r)} data-testid={`rule-role-${r}`} />
                    {roleLabel(r)}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[13px] font-medium">Vendor Types <span className="text-slate-400 font-normal">(blank = any)</span></label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {VENDOR_TYPES.map((t) => (
                  <label key={t.value} className="flex items-center gap-1.5 text-[12.5px] border border-slate-200 rounded-full px-2.5 py-1">
                    <input type="checkbox" checked={form.vendorTypes.includes(t.value)} onChange={() => toggle("vendorTypes", t.value)} data-testid={`rule-vendor-type-${t.value}`} />
                    {t.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[13px] font-medium">Purchasing Context Types <span className="text-slate-400 font-normal">(blank = any)</span></label>
              <div className="mt-1.5 flex flex-wrap gap-2">
                {CONTEXT_TYPES.map((t) => (
                  <label key={t.value} className="flex items-center gap-1.5 text-[12.5px] border border-slate-200 rounded-full px-2.5 py-1">
                    <input type="checkbox" checked={form.contextTypes.includes(t.value)} onChange={() => toggle("contextTypes", t.value)} data-testid={`rule-context-type-${t.value}`} />
                    {t.label}
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="text-[13px] font-medium">Escalation <span className="text-slate-400 font-normal">(optional)</span></label>
              <input type="number" min="0" value={form.escalationHours} onChange={(e) => setForm({ ...form, escalationHours: e.target.value })} placeholder="Hours before a pending step escalates" data-testid="rule-escalation-hours-input" className="mt-1.5 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>

            <button onClick={create} data-testid="confirm-create-rule-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Create Rule</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
